/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
    Box,
    Card,
    Typography,
    LinearProgress,
    Stack,
    Chip,
    Collapse,
    Alert,
    Button,
    Skeleton,
    IconButton,
    Tooltip,
    AvatarGroup,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { error as errorPalette, warning as warningPalette, info as infoPalette, success as successPalette, grey as greyPalette } from 'src/theme/palette';
import { CATEGORY_LABELS, STATUS_COLORS } from 'src/constants/TaskConstants';
import { CHECKLIST_ITEM_TYPE, CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import TaskViewer from '../../tasks/TaskViewer';
import IosEmailComposer from '../../tasks/composers/IosEmailComposer';
import IosWhatsAppComposerDialog from '../../tasks/composers/IosWhatsAppComposerDialog';

// -----------------------------------------------

const MAC = {
    cardBg: '#FFFFFF',
    border: 'rgba(0, 0, 0, 0.06)',
    borderHover: 'rgba(0, 0, 0, 0.10)',
    text: '#1D1D1F',
    textSecondary: '#86868B',
    textTertiary: '#A1A1A6',
    accent: '#007AFF',
    success: '#34C759',
    warning: '#FF9500',
    danger: '#FF3B30',
    shadowSm: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
    shadowMd: '0 2px 4px rgba(0,0,0,0.06), 0 8px 16px rgba(0,0,0,0.04)',
};
const MAC_FONT_STACK = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", system-ui, sans-serif';

// WCAG AA text colours for alpha-0.10 chip backgrounds — .dark variants pass 4.5:1 on white.
const STATUS_TEXT_COLORS = {
    pending:     warningPalette.dark,   // #B76E00
    in_progress: infoPalette.dark,      // #006C9C
    completed:   successPalette.dark,   // #118D57
    archived:    greyPalette[700],      // #454F5B
};

// -----------------------------------------------

const ACTION_ITEM_TYPES = [CHECKLIST_ITEM_TYPE.EMAIL_SENT, CHECKLIST_ITEM_TYPE.WHATSAPP_SENT];
const ACTION_BUTTON_META = {
    [CHECKLIST_ITEM_TYPE.EMAIL_SENT]:    { label: 'Send Email',    icon: 'eva:email-outline',   color: '#078DEE' },
    [CHECKLIST_ITEM_TYPE.WHATSAPP_SENT]: { label: 'Send WhatsApp', icon: 'ri:whatsapp-fill',    color: '#25D366' },
};

function TemplateGroup({ group, onTaskClick, onComposerOpen, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    // BE no longer creates a parent summary Task (fix shipped 2026-05-25 —
    // TaskService.create_from_template now creates only leaf child Tasks with
    // parent_task=None). All tasks in the group are real action items.
    const visibleTasks = Array.isArray(group.tasks) ? group.tasks : [];
    const total = visibleTasks.length;
    const completed = visibleTasks.filter((t) => t.status === 'completed').length;
    const progressPct = total > 0 ? (completed / total) * 100 : 0;
    const appliedDate = group.applied_at
        ? new Date(group.applied_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
        : '—';
    const appliedBy = group.applied_by?.name || '—';

    return (
        <Box
            sx={{
                border: `1px solid ${MAC.border}`,
                borderRadius: '10px',
                p: '14px',
                bgcolor: MAC.cardBg,
                fontFamily: MAC_FONT_STACK,
                transition: 'border-color 200ms ease',
                '&:hover': { borderColor: MAC.borderHover },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: MAC_FONT_STACK, fontWeight: 500, fontSize: '13px', color: MAC.text }}>
                        {group.template_name}
                    </Typography>
                    {group.template_category && CATEGORY_LABELS[group.template_category] && (
                        <Chip
                            label={CATEGORY_LABELS[group.template_category]}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '11px',
                                fontWeight: 500,
                                bgcolor: 'rgba(0, 122, 255, 0.10)',
                                color: MAC.accent,
                                borderRadius: '6px',
                                '& .MuiChip-label': { px: '8px' },
                            }}
                        />
                    )}
                    {group.template_scope && (
                        <Chip
                            label={group.template_scope}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '11px',
                                fontWeight: 500,
                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                                color: MAC.textSecondary,
                                borderRadius: '6px',
                                border: 'none',
                                textTransform: 'capitalize',
                                '& .MuiChip-label': { px: '8px' },
                            }}
                        />
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
                        <Iconify icon="eva:more-vertical-fill" width={14} sx={{ color: MAC.textTertiary }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                        <Iconify
                            icon="eva:chevron-down-fill"
                            width={14}
                            sx={{ transition: 'transform 200ms ease', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                    </IconButton>
                </Stack>
            </Stack>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem
                    onClick={() => { setMenuAnchor(null); onDelete(group); }}
                    sx={{ color: 'error.main', minWidth: 200 }}
                >
                    <ListItemIcon sx={{ color: 'error.main' }}>
                        <Iconify icon="eva:trash-2-outline" width={18} />
                    </ListItemIcon>
                    <ListItemText>Remove this checklist</ListItemText>
                </MenuItem>
            </Menu>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <LinearProgress
                    variant="determinate"
                    value={progressPct}
                    sx={{
                        flex: 1,
                        height: 3,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(0, 0, 0, 0.06)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 1.5,
                            bgcolor: progressPct === 100 ? MAC.success : MAC.accent,
                            transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                    }}
                />
                <Typography sx={{ fontFamily: MAC_FONT_STACK, color: MAC.textSecondary, whiteSpace: 'nowrap', fontSize: '11px', fontWeight: 500 }}>
                    {completed} / {total} complete
                </Typography>
            </Stack>
            <Typography sx={{ fontFamily: MAC_FONT_STACK, fontSize: '11px', fontWeight: 400, color: MAC.textTertiary }}>
                Applied {appliedDate} by {appliedBy}
            </Typography>

            <Collapse in={expanded}>
                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                    {visibleTasks.length === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 1,
                                px: 1.5,
                                borderRadius: 1,
                                border: '1px dashed',
                                borderColor: 'divider',
                                bgcolor: 'action.hover',
                            }}
                        >
                            <Iconify icon="eva:clock-outline" width={14} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.disabled', fontStyle: 'italic' }}>
                                Cells unlock once the consultation is attended
                            </Typography>
                        </Box>
                    )}
                    {visibleTasks.map((task) => {
                        const isOverdue = task.due_at && task.status !== 'completed' && new Date(task.due_at) < new Date();
                        const dueLabel = task.due_at
                            ? new Date(task.due_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                            : null;
                        const completedLabel = task.status === 'completed' && task.completed_at
                            ? new Date(task.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                            : null;
                        const assignees = task.assignees || [];

                        const pendingActionItem = task.checklist_items?.find(
                            (i) => i.completion_state === CHECKLIST_COMPLETION_STATE.PENDING
                                && ACTION_ITEM_TYPES.includes(i.item_type)
                        );
                        const actionMeta = pendingActionItem ? ACTION_BUTTON_META[pendingActionItem.item_type] : null;

                        return (
                            <Box
                                key={task.id}
                                onClick={() => onTaskClick(task, group)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    py: '8px',
                                    px: '10px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'background-color 150ms ease',
                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.025)' },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: MAC_FONT_STACK,
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: '13px',
                                        fontWeight: 400,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        letterSpacing: '-0.01em',
                                        textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                                        color: task.status === 'completed' ? MAC.textSecondary : MAC.text,
                                    }}
                                >
                                    {task.title}
                                </Typography>
                                {/* Subtask identifier. Operators reported confusing
                                    template-spawned child Task rows with the
                                    parent Task above them (both render as
                                    similarly-styled rows inside the group).
                                    We surface a small chip ONLY when the row
                                    has a parent_task_id — top-level tasks
                                    stay chip-free to keep the visual quiet
                                    on the common case. The label "Subtask"
                                    is the existing data-model term so we
                                    don't invent new vocabulary. */}
                                {task.parent_task_id && (
                                    <Tooltip
                                        title="Sub-task of the parent task above"
                                        placement="top"
                                        arrow
                                    >
                                        <Chip
                                            label="Subtask"
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontFamily: MAC_FONT_STACK,
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                                color: MAC.textSecondary,
                                                borderRadius: '6px',
                                                flexShrink: 0,
                                                '& .MuiChip-label': { px: '8px' },
                                            }}
                                        />
                                    </Tooltip>
                                )}
                                {task.status && STATUS_COLORS[task.status] && (
                                    <Chip
                                        label={task.status.replace('_', ' ')}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontFamily: MAC_FONT_STACK,
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            bgcolor: alpha(STATUS_COLORS[task.status], 0.10),
                                            color: STATUS_TEXT_COLORS[task.status] || STATUS_COLORS[task.status],
                                            textTransform: 'capitalize',
                                            flexShrink: 0,
                                            borderRadius: '6px',
                                            '& .MuiChip-label': { px: '8px' },
                                        }}
                                    />
                                )}
                                {dueLabel && (
                                    <Typography
                                        variant="caption"
                                        sx={{ fontSize: '0.7rem', color: isOverdue ? errorPalette.main : 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}
                                    >
                                        {dueLabel}
                                    </Typography>
                                )}
                                {completedLabel && (() => {
                                    const actor = task.completed_by_practice_user || null;
                                    const fullName = actor?.full_name
                                        || task.completed_by_full_name
                                        || (typeof task.completed_by === 'string' ? task.completed_by : '')
                                        || (task.completed_by && typeof task.completed_by === 'object'
                                            ? `${task.completed_by.first_name || ''} ${task.completed_by.last_name || ''}`.trim() || task.completed_by.email
                                            : '');
                                    const avatarUrl = actor?.avatar_url || task.completed_by_avatar_url || undefined;
                                    const initial = (fullName || '?').charAt(0).toUpperCase() || '?';
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
                                            {fullName && (
                                                <Avatar src={avatarUrl} sx={{ width: 14, height: 14, fontSize: '0.55rem', flexShrink: 0 }}>
                                                    {initial}
                                                </Avatar>
                                            )}
                                            <Typography
                                                variant="caption"
                                                sx={{ fontSize: '0.7rem', color: 'success.main', whiteSpace: 'nowrap' }}
                                            >
                                                Done{fullName ? ` · ${fullName}` : ''} {completedLabel}
                                            </Typography>
                                        </Box>
                                    );
                                })()}
                                {assignees.length > 0 && (
                                    <AvatarGroup max={3} sx={{ flexShrink: 0, '& .MuiAvatar-root': { width: 20, height: 20, fontSize: '0.6rem' } }}>
                                        {assignees.map((a, i) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <Tooltip key={i} title={a.name || ''}>
                                                <Avatar src={a.avatar || ''} sx={{ width: 20, height: 20 }}>
                                                    {(a.name || '?')[0]}
                                                </Avatar>
                                            </Tooltip>
                                        ))}
                                    </AvatarGroup>
                                )}
                                {actionMeta && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onComposerOpen(pendingActionItem, task);
                                        }}
                                        sx={{
                                            height: 24, fontSize: '0.7rem', textTransform: 'none', flexShrink: 0,
                                            bgcolor: actionMeta.color, px: 1, borderRadius: 1,
                                            '&:hover': { bgcolor: actionMeta.color, filter: 'brightness(0.9)' },
                                        }}
                                        startIcon={<Iconify icon={actionMeta.icon} width={12} />}
                                    >
                                        {actionMeta.label}
                                    </Button>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Collapse>
        </Box>
    );
}

TemplateGroup.propTypes = {
    group: PropTypes.shape({
        template_id: PropTypes.any,
        template_name: PropTypes.string,
        template_category: PropTypes.string,
        template_scope: PropTypes.string,
        applied_at: PropTypes.string,
        applied_by: PropTypes.shape({ id: PropTypes.any, name: PropTypes.string }),
        tasks: PropTypes.array,
        progress: PropTypes.shape({ completed: PropTypes.number, total: PropTypes.number }),
    }).isRequired,
    onTaskClick: PropTypes.func.isRequired,
    onComposerOpen: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

// -----------------------------------------------

export default function TaskChecklistCard({ practiceId, leadId, leadData, onGroupsLoaded }) {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [slideoutOpen, setSlideoutOpen] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [composerItem, setComposerItem] = useState(null); // { item_type, action_config }
    const [composerOpen, setComposerOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // group to delete
    const [deleting, setDeleting] = useState(false);

    const fetchGroups = async () => {
        if (!practiceId || !leadId) return;
        // Match the BE `tasks-v2` permission gate. Without this, lead-detail
        // pages flood Sentry with PermissionDenied 403s on
        // /task-checklists/ for users on practices that don't have tasks-v2
        // (DENTALSEM-CRM-1D6X / 1D7X-series). practiceId is passed so the
        // gate is robust to the practice-switch window.
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setGroups([]);
            onGroupsLoaded([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setFetchError(null);
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`);
            if (res.status === 200) {
                let data;
                if (Array.isArray(res.data)) {
                    data = res.data;
                } else if (Array.isArray(res.data?.results)) {
                    data = res.data.results;
                } else {
                    data = [];
                }
                setGroups(data);
                onGroupsLoaded(data.flatMap((g) => g.tasks.map((t) => String(t.id))));
            } else if (res.status === 404) {
                setGroups([]);
            } else {
                Sentry.captureMessage('task_checklist_card_unexpected_status', {
                    tags: { status: String(res.status), practice_id: String(practiceId), lead_id: String(leadId) },
                });
                setFetchError('Failed to load task checklists.');
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'task_checklist_card_fetch' } });
            setFetchError('Failed to load task checklists.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, [practiceId, leadId]);

    const handleTaskClick = (task, group) => {
        setSelectedTaskId(String(task.id));
        setSelectedGroup(group || null);
        setSlideoutOpen(true);
    };

    const handleSlideoutClose = () => {
        setSlideoutOpen(false);
        setSelectedTaskId(null);
        setSelectedGroup(null);
    };

    const handleSlideoutDeleteChecklist = () => {
        handleSlideoutClose();
        if (selectedGroup) handleDeleteGroup(selectedGroup);
    };

    const handleComposerOpen = (item) => {
        setComposerItem(item);
        setComposerOpen(true);
    };

    const handleComposerClose = () => {
        setComposerOpen(false);
        setComposerItem(null);
    };

    const handleComposerSend = () => {
        handleComposerClose();
        fetchGroups();
    };

    const handleDeleteGroup = (group) => {
        setDeleteTarget(group);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        // Find the root parent task (parent_task_id == null) as the target for DELETE
        const rootTask = deleteTarget.tasks?.find((t) => !t.parent_task_id) || deleteTarget.tasks?.[0];
        if (!rootTask?.id) {
            dispatch(updateError({ success: false, message: 'Unable to identify checklist root task.' }));
            setDeleteTarget(null);
            return;
        }
        setDeleting(true);
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/tasks/${rootTask.id}/`, 'delete');
            if (res.status === 204 || res.status === 200) {
                setDeleteTarget(null);
                fetchGroups();
            } else {
                dispatch(updateError({ success: false, message: res.data?.detail || 'Failed to remove checklist.' }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'delete_applied_checklist' } });
            dispatch(updateError({ success: false, message: 'Failed to remove checklist.' }));
        } finally {
            setDeleting(false);
        }
    };

    const cardSx = {
        p: '14px',
        mb: 1.5,
        borderRadius: '10px',
        border: `1px solid ${MAC.border}`,
        boxShadow: MAC.shadowSm,
        bgcolor: MAC.cardBg,
        fontFamily: MAC_FONT_STACK,
        transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': { boxShadow: MAC.shadowMd },
    };
    const headingSx = {
        fontFamily: MAC_FONT_STACK,
        fontWeight: 500,
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: MAC.textSecondary,
        mb: 1.5,
    };

    if (loading) {
        return (
            <Card sx={cardSx}>
                <Typography variant="subtitle2" sx={headingSx}>Active Task Checklists</Typography>
                <Stack spacing={1}>
                    {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 1.5 }} />)}
                </Stack>
            </Card>
        );
    }

    if (fetchError) {
        return (
            <Card sx={cardSx}>
                <Typography variant="subtitle2" sx={headingSx}>Active Task Checklists</Typography>
                <Alert severity="warning" action={<Button size="small" onClick={fetchGroups}>Retry</Button>}>
                    {fetchError}
                </Alert>
            </Card>
        );
    }

    if (groups.length === 0) {
        return (
            <Card sx={cardSx}>
                <Typography variant="subtitle2" sx={headingSx}>Active Task Checklists</Typography>
                <Box sx={{ py: 2.5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                    <Typography variant="caption" color="text.disabled">
                        No task checklists applied to this lead. Apply a template from the Tasks section to track structured workflows.
                    </Typography>
                </Box>
            </Card>
        );
    }

    return (
        <>
            <Card sx={cardSx}>
                <Typography variant="subtitle2" sx={headingSx}>Active Task Checklists</Typography>
                <Stack spacing={1}>
                    {groups.map((group, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <TemplateGroup
                            key={`${group.template_id}-${i}`}
                            group={group}
                            onTaskClick={handleTaskClick}
                            onComposerOpen={handleComposerOpen}
                            onDelete={handleDeleteGroup}
                        />
                    ))}
                </Stack>
            </Card>
            <TaskViewer
                open={slideoutOpen}
                onClose={handleSlideoutClose}
                taskId={selectedTaskId}
                practiceId={practiceId}
                onTaskUpdated={fetchGroups}
                onDeleteChecklist={selectedGroup ? handleSlideoutDeleteChecklist : null}
            />
            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => !deleting && setDeleteTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Remove this applied checklist?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently remove <strong>{deleteTarget?.template_name}</strong> from this lead.
                        The template itself won&apos;t be deleted — only this lead&apos;s instance will be removed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                    >
                        {deleting ? 'Removing…' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>
            {composerOpen && composerItem?.item_type === CHECKLIST_ITEM_TYPE.EMAIL_SENT && (
                <IosEmailComposer
                    open={composerOpen}
                    onClose={handleComposerClose}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadData || { id: leadId }}
                    templateId={composerItem.action_config?.template_id || null}
                    onSendSuccess={handleComposerSend}
                />
            )}
            {composerOpen && composerItem?.item_type === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT && (
                <IosWhatsAppComposerDialog
                    open={composerOpen}
                    onClose={handleComposerClose}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadData || { id: leadId }}
                    phoneNumber={leadData?.lead_contact?.phone || leadData?.phone || ''}
                    onSendSuccess={handleComposerSend}
                />
            )}
        </>
    );
}

TaskChecklistCard.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadData: PropTypes.object,
    onGroupsLoaded: PropTypes.func,
};

TaskChecklistCard.defaultProps = {
    leadData: null,
    onGroupsLoaded: () => {},
};
